// The Mail workspace's "Re-bill to customer" (Session AUF, Director decision DL-3), shown on an examining body's invoice or
// notice email. The office picks which of the customers WE arrange examinations for it is, then the customer page's own
// re-bill form (CustomerLolerArrangementSection, rebillOnly) raises the one Xero DRAFT line, carrying this email so the same
// invoice is not re-billed twice. Only arranged customers are offered: anyone else's examining body invoices them directly.
import { useState } from "react";
import { api } from "../../../lib/api";
import type { LolerArrangement } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { CustomerLolerArrangementSection } from "../CustomerLolerArrangementSection";

const LINK_BTN = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";
const FIELD = "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

export function MailLolerRebill({ inboundEmailId }: { inboundEmailId: string }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState<LolerArrangement[] | null>(null);
  const [customerId, setCustomerId] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setOpen(true);
    setError(null);
    try {
      setCustomers((await api.customers.lolerArrangements()).customers);
    } catch (e) {
      setError(friendlyApiError(e, "Could not load the customers we arrange examinations for."));
    }
  }

  if (!open) {
    return (
      <button type="button" className={LINK_BTN} onClick={() => void start()}>
        Re-bill to customer
      </button>
    );
  }
  return (
    <section aria-label="Re-bill to customer" className="space-y-2 rounded-ele border border-ele-hairline p-3" data-testid="mail-loler-rebill">
      <p className="text-xs text-ele-body">An examining body's invoice for a customer we arrange examinations for, re-billed to them as one Xero draft line.</p>
      {error && (
        <p role="alert" className="text-sm text-ele-danger">
          {error}
        </p>
      )}
      {customers && customers.length === 0 && <p className="text-sm text-ele-body">We do not arrange examinations for any customer, so there is nothing to re-bill.</p>}
      {customers && customers.length > 0 && (
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Which customer</span>
          <select aria-label="Which customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={FIELD}>
            <option value="">Choose a customer</option>
            {customers.map((c) => (
              <option key={c.customerId} value={c.customerId}>
                {c.customerName}
              </option>
            ))}
          </select>
        </label>
      )}
      {customerId && <CustomerLolerArrangementSection key={customerId} customerId={customerId} inboundEmailId={inboundEmailId} rebillOnly />}
      <button type="button" className={LINK_BTN} onClick={() => setOpen(false)}>
        Close
      </button>
    </section>
  );
}
